import { LegalPage } from './LegalPage';

const CONTACT_EMAIL = 'support@tenkventures.com';
const GOVERNING_STATE = 'New York';
const UPDATED = 'May 14, 2026';

export function TermsScreen() {
  return (
    <LegalPage title="Terms of Service" updatedDate={UPDATED}>
      <p>
        Welcome to Lumé. These Terms govern your use of lume.skin (the "Service") operated
        by Ten Ventures ("we", "us"). By using the Service or paying for a plan, you agree
        to these Terms.
      </p>

      <h2>1. What Lumé does</h2>
      <p>
        Lumé takes a photo of your face, analyses it for visible skin signals (wrinkles,
        pigmentation, hydration, etc.), estimates a "skin age" relative to your real age,
        and generates a personalised skincare routine using AI.
      </p>
      <p>
        <strong>Lumé is a cosmetic tool, not a medical service.</strong> See section 5.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 13 years old to use Lumé. The scan flow requires you to enter
        your age. By using Lumé, you represent that you are 13 or older.
      </p>

      <h2>3. Your plan — what you're paying for</h2>
      <p>
        For a one-time fee of $8.99 USD (the displayed promotional price; standard price
        $11.99), you receive:
      </p>
      <ul>
        <li>A personalised AM and PM skincare routine generated for your scan;</li>
        <li>An ingredient priority list matched to your scan;</li>
        <li>A bookmarkable URL where you can access your plan for up to 90 days;</li>
        <li>An emailed copy of the plan link (if email delivery is enabled).</li>
      </ul>
      <p>
        Your purchase is a <strong>one-time payment</strong>. There is no subscription, no
        auto-renewal, and no recurring charge.
      </p>

      <h2>4. Refund policy</h2>
      <p>
        Because Lumé generates personalised AI content the moment you pay (and the plan is
        immediately delivered to a unique URL), purchases are <strong>generally
        non-refundable</strong>.
      </p>
      <p>
        That said: if your plan failed to generate, the link doesn't work, or something
        clearly broke on our end, email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> within 7 days and we'll
        refund you. We're reasonable.
      </p>

      <h2>5. Not medical advice</h2>
      <p>
        Lumé's recommendations are <strong>cosmetic</strong> in nature and generated by AI.
        They are NOT:
      </p>
      <ul>
        <li>A medical diagnosis;</li>
        <li>A substitute for advice from a dermatologist or other healthcare professional;</li>
        <li>Suitable for treating skin conditions such as acne, rosacea, eczema, melanoma,
            or any other dermatological condition.</li>
      </ul>
      <p>
        Always patch-test new products on a small area of skin before applying broadly.
        Discontinue use of any product that causes irritation, redness, or other adverse
        reaction. <strong>For any persistent skin concern, see a licensed
        dermatologist.</strong>
      </p>
      <p>
        We do not guarantee specific results. Individual results vary based on skin type,
        consistency, diet, sleep, environment, genetics, and other factors outside our
        control.
      </p>

      <h2>6. Product links</h2>
      <p>
        Your plan may include links to third-party products (e.g., from CeraVe, The
        Ordinary, Paula's Choice). We are not affiliated with these brands. We may earn
        affiliate commissions on some links; if so, that does not affect what we recommend.
      </p>
      <p>
        We are not responsible for product availability, pricing changes, shipping, or any
        issues with third-party retailers.
      </p>

      <h2>7. Your content (your photo)</h2>
      <p>
        You retain all ownership of the photos you submit. You grant us a limited license
        to use your photo solely for the purposes of:
      </p>
      <ul>
        <li>Processing it through our skin analysis provider;</li>
        <li>Displaying it back to you in before/after comparisons;</li>
        <li>Storing it for up to 90 days as described in our Privacy Policy.</li>
      </ul>
      <p>
        We will not use your photo for marketing, training AI models, or any other purpose
        without separate, explicit permission.
      </p>

      <h2>8. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Upload images of anyone other than yourself without their consent;</li>
        <li>Use Lumé to harass, deceive, or impersonate;</li>
        <li>Attempt to reverse-engineer, scrape, or abuse the Service;</li>
        <li>Use Lumé in violation of any law.</li>
      </ul>

      <h2>9. Service availability</h2>
      <p>
        We try to keep Lumé available, but the Service is provided "as is" and "as
        available". We do not guarantee uptime, error-free operation, or that the AI
        analysis will always succeed. If a scan fails after payment, see section 4
        (refunds).
      </p>

      <h2>10. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, our total liability to you for any claim
        arising out of or relating to the Service is limited to the amount you paid us in
        the 12 months preceding the claim. We are not liable for any indirect, incidental,
        consequential, or punitive damages.
      </p>
      <p>
        Some jurisdictions do not allow these limitations, in which case the limitations
        apply to the extent permitted.
      </p>

      <h2>11. Indemnification</h2>
      <p>
        You agree to indemnify and hold Ten Ventures harmless from any claims arising from
        your misuse of the Service, your violation of these Terms, or your violation of any
        rights of a third party (including uploading photos of others without consent).
      </p>

      <h2>12. Termination</h2>
      <p>
        We may suspend or terminate access at our discretion for breach of these Terms. On
        termination, your plan link may stop working. You can stop using Lumé any time.
      </p>

      <h2>13. Governing law</h2>
      <p>
        These Terms are governed by the laws of the State of {GOVERNING_STATE}, USA,
        without regard to its conflict of law principles. Any dispute will be resolved in
        the state or federal courts located in {GOVERNING_STATE}.
      </p>

      <h2>14. Changes</h2>
      <p>
        We may update these Terms. If we make material changes, we'll post a notice and
        update the "Last updated" date. Continued use after changes means you accept the
        revised Terms.
      </p>

      <h2>15. Contact</h2>
      <p>
        Questions about these Terms:{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </LegalPage>
  );
}
